import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';

function Tooltip({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '6px' }}>
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '16px', height: '16px', borderRadius: '50%',
          backgroundColor: '#94a3b8', color: 'white',
          fontSize: '10px', fontWeight: 'bold', cursor: 'help', flexShrink: 0,
          lineHeight: 1, userSelect: 'none'
        }}
      >?</span>
      {visible && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#1e293b', color: 'white',
          padding: '8px 12px', borderRadius: '6px',
          fontSize: '13px', lineHeight: '1.5',
          whiteSpace: 'pre-line', width: '260px',
          zIndex: 1000, boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          pointerEvents: 'none', textAlign: 'left', fontWeight: 'normal'
        }}>
          {text}
          <span style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            border: '5px solid transparent', borderTopColor: '#1e293b'
          }} />
        </span>
      )}
    </span>
  );
}

function TournamentCreate() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { plan, limits } = usePlan();

  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: '',
    num_teams: '8',
    num_courts: '2',
    num_groups: '4',
    tournament_type: 'ko',
    group_assignment_mode: 'auto',
    teams_advance_from_group: '2',
    max_rounds: '5',
    ko_consolation_round: false,
    best_of_three: false,
    show_in_presentation: true,
    start_time: '',
    match_duration: '20',
    break_duration: '5',
    draw_mode: 'random',
  });

  const [courtNames, setCourtNames] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [allowedTypes, setAllowedTypes] = useState(null); // null = alle erlaubt (user-level)

  // Effektiv erlaubte Typen: Schnittmenge aus User-Rechten und Plan-Limits
  const effectiveAllowedTypes = React.useMemo(() => {
    const planTypes = limits?.allowed_tournament_types || null;
    if (!allowedTypes && !planTypes) return null;
    if (!allowedTypes) return planTypes;
    if (!planTypes) return allowedTypes;
    return allowedTypes.filter(t => planTypes.includes(t));
  }, [allowedTypes, limits]);

  const maxCourts = limits?.max_courts || null;

  useEffect(() => {
    axios.get('/api/users/me').then(res => {
      const types = res.data.allowed_tournament_types;
      if (types && types.length > 0) setAllowedTypes(types);
    }).catch(() => {});
  }, []);

  // Sicherstellen dass gewählter Typ nach Laden der Limits noch erlaubt ist
  useEffect(() => {
    if (effectiveAllowedTypes && effectiveAllowedTypes.length > 0) {
      setFormData(prev => ({
        ...prev,
        tournament_type: effectiveAllowedTypes.includes(prev.tournament_type)
          ? prev.tournament_type
          : effectiveAllowedTypes[0]
      }));
    }
  }, [effectiveAllowedTypes]);

  const isTypeAllowed = (type) => {
    if (!effectiveAllowedTypes || effectiveAllowedTypes.length === 0) return true;
    return effectiveAllowedTypes.includes(type);
  };

  // Update court names when num_courts changes
  useEffect(() => {
    const numCourts = parseInt(formData.num_courts) || 0;
    if (numCourts > 0) {
      const newCourtNames = Array.from({ length: numCourts }, (_, i) =>
        courtNames[i] || `Court ${i + 1}`
      );
      setCourtNames(newCourtNames);
    } else {
      setCourtNames([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.num_courts]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCourtNameChange = (index, value) => {
    const updated = [...courtNames];
    updated[index] = value;
    setCourtNames(updated);
  };

  const validateForm = () => {
    const errors = [];

    // Validate name
    if (!formData.name || formData.name.trim().length < 3) {
      errors.push('Turniername muss mindestens 3 Zeichen lang sein');
    }

    // Validate date
    if (!formData.date) {
      errors.push('Datum ist erforderlich');
    }

    // Validate num_teams
    const numTeams = parseInt(formData.num_teams);
    if (isNaN(numTeams) || numTeams < 2 || numTeams > 64) {
      errors.push('Anzahl Teams muss zwischen 2 und 64 liegen');
    }

    // Validate num_courts
    const numCourts = parseInt(formData.num_courts);
    if (isNaN(numCourts) || numCourts < 1 || numCourts > 20) {
      errors.push('Anzahl Courts muss zwischen 1 und 20 liegen');
    }
    if (maxCourts && numCourts > maxCourts) {
      errors.push(`Dein Plan erlaubt maximal ${maxCourts} Courts. Bitte upgraden.`);
    }

    // Validate num_groups for groups tournament
    if (formData.tournament_type === 'groups') {
      const numGroups = parseInt(formData.num_groups);
      if (isNaN(numGroups) || numGroups < 2 || numGroups > 8 || numGroups % 2 !== 0) {
        errors.push('Anzahl Gruppen muss eine gerade Zahl zwischen 2 und 8 sein');
      }
    }

    // Validate court names
    if (courtNames.some(name => !name || name.trim().length === 0)) {
      errors.push('Alle Court-Namen müssen ausgefüllt sein');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(' | '));
      setLoading(false);
      return;
    }

    try {
      // Prepare payload
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        date: formData.date,
        num_teams: parseInt(formData.num_teams),
        num_courts: parseInt(formData.num_courts),
        court_names: courtNames.join(', '),
        tournament_type: formData.tournament_type,
        is_active: 0,
      };

      // Add num_groups, group_assignment_mode and teams_advance_from_group only for groups tournament
      if (formData.tournament_type === 'groups') {
        payload.num_groups = parseInt(formData.num_groups);
        payload.group_assignment_mode = formData.group_assignment_mode;
        // teams_advance_from_group kann String oder Number sein
        payload.teams_advance_from_group = formData.teams_advance_from_group === 'best_plus_runners'
          ? 'best_plus_runners'
          : parseInt(formData.teams_advance_from_group);
      }

      // Zeitplanung hinzufügen (für groups und ko_placement)
      if (formData.tournament_type === 'groups' || formData.tournament_type === 'ko_placement') {
        if (formData.start_time) {
          payload.start_time = formData.start_time;
        }
        if (formData.match_duration) {
          payload.match_duration = parseInt(formData.match_duration);
        }
        if (formData.break_duration) {
          payload.break_duration = parseInt(formData.break_duration);
        }
      }

      // Add max_rounds for mexicano tournament (user input)
      if (formData.tournament_type === 'mexicano') {
        payload.max_rounds = parseInt(formData.max_rounds);
      }

      // Add max_rounds for americano tournament (automatic: num_teams - 1)
      if (formData.tournament_type === 'americano') {
        payload.max_rounds = parseInt(formData.num_teams) - 1;
      }

      // Add ko_consolation_round for ko tournament
      if (formData.tournament_type === 'ko') {
        payload.ko_consolation_round = formData.ko_consolation_round ? 1 : 0;
      }

      // Add draw_mode for ko_placement tournament
      if (formData.tournament_type === 'ko_placement') {
        payload.draw_mode = formData.draw_mode || 'random';
      }

      // Add best_of_three (available for all tournament types)
      payload.best_of_three = formData.best_of_three ? 1 : 0;
      payload.show_in_presentation = formData.show_in_presentation ? 1 : 0;

      console.log('DEBUG: formData.best_of_three =', formData.best_of_three);
      console.log('DEBUG: payload.best_of_three =', payload.best_of_three);
      console.log('Creating tournament with payload:', payload);

      // Create tournament
      const tournamentResponse = await axios.post('/api/tournaments', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const tournamentId = tournamentResponse.data.id;

      setMessage(t('create.success'));
      setTimeout(() => {
        navigate(`/admin/edit/${tournamentId}`);
      }, 1500);

    } catch (err) {
      console.error('Fehler beim Erstellen:', err);
      console.error('Fehlerdetails:', err.response?.data);

      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail.map(error => `${error.loc.join('.')} - ${error.msg}`).join(' | ');
          setError(`Validierungsfehler: ${errorMessages}`);
        } else {
          setError(`Fehler: ${err.response.data.detail}`);
        }
      } else {
        setError(t('create.error'));
      }
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: 700, margin: '0 auto' }}>
      <h2>{t('create.title')}</h2>
      <p style={{ color: '#6c757d', marginBottom: '20px' }}>
        {t('create.subtitle')}
      </p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center' }}>
            {t('create.name')}
            <Tooltip text={t('create.tooltipName')} />
          </label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder={t('create.placeholderName')}
          />
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center' }}>
            {t('create.date')}
            <Tooltip text={t('create.tooltipDate')} />
          </label>
          <input
            name="date"
            type="date"
            value={formData.date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
            {t('create.format')} {t('create.formatNote')}
            <Tooltip text={t('create.tooltipFormat')} />
          </label>
          <select
            name="tournament_type"
            value={formData.tournament_type}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid rgba(206, 212, 218, 0.7)',
              backgroundColor: 'rgba(245, 242, 239, 0.9)',
              color: '#0F172A',
              fontSize: '1rem',
              marginTop: '8px'
            }}
            required
          >
            <option value="ko" disabled={!isTypeAllowed('ko')}>KO-Turnier (Single Elimination){!isTypeAllowed('ko') ? ` ${t('create.notAllowed')}` : ''}</option>
            <option value="mexicano" disabled={!isTypeAllowed('mexicano')}>Mexicano{!isTypeAllowed('mexicano') ? ` ${t('create.notAllowed')}` : ''}</option>
            <option value="americano" disabled={!isTypeAllowed('americano')}>Americano{!isTypeAllowed('americano') ? ` ${t('create.notAllowed')}` : ''}</option>
            <option value="groups" disabled={!isTypeAllowed('groups')}>Gruppenphase + K.O.-Finale{!isTypeAllowed('groups') ? ` ${t('create.notAllowed')}` : ''}</option>
            <option value="ko_placement" disabled={!isTypeAllowed('ko_placement')}>KO mit Monrad-Platzierungen (8 / 16 Teams){!isTypeAllowed('ko_placement') ? ` ${t('create.notAllowed')}` : ''}</option>
          </select>
          <small style={{ color: '#fb923c', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>
            {t('create.formatWarning')}
          </small>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center' }}>
            {(formData.tournament_type === 'mexicano' || formData.tournament_type === 'americano') ? t('create.numPlayers') : t('create.numTeams')}
            <Tooltip text={
              (formData.tournament_type === 'mexicano' || formData.tournament_type === 'americano')
                ? t('create.tooltipNumTeamsMexicano')
                : formData.tournament_type === 'ko_placement'
                ? t('create.tooltipNumTeamsKoPlacement')
                : t('create.tooltipNumTeams')
            } />
          </label>
          {formData.tournament_type === 'ko_placement' ? (
            <select
              name="num_teams"
              value={formData.num_teams}
              onChange={handleChange}
              required
              style={{
                width: '100%', padding: '10px', borderRadius: '6px',
                border: '1px solid rgba(206, 212, 218, 0.7)',
                backgroundColor: 'rgba(245, 242, 239, 0.9)',
                color: '#0F172A', fontSize: '1rem', marginTop: '8px'
              }}
            >
              <option value="">{t('create.numTeamsChoose')}</option>
              <option value="8">8 Teams</option>
              <option value="16">16 Teams</option>
            </select>
          ) : (
            <input
              name="num_teams"
              type="number"
              min="2"
              max="64"
              value={formData.num_teams}
              onChange={handleChange}
              required
              placeholder={t('create.placeholderNumTeams')}
            />
          )}
          <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
            {(formData.tournament_type === 'mexicano' || formData.tournament_type === 'americano')
              ? t('create.helpNumTeamsMexicano')
              : formData.tournament_type === 'ko_placement'
              ? t('create.helpNumTeamsKoPlacement')
              : t('create.helpNumTeams')}
          </small>
        </div>

        {formData.tournament_type === 'groups' && (
          <>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                {t('create.numGroups')}
                <Tooltip text={t('create.tooltipNumGroups')} />
              </label>
              <input
                name="num_groups"
                type="number"
                min="2"
                max="8"
                step="2"
                value={formData.num_groups}
                onChange={handleChange}
                required
                placeholder={t('create.placeholderNumGroups')}
              />
              <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                {t('create.helpNumGroups')}
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                {t('create.groupAssignment')}
                <Tooltip text={t('create.tooltipGroupAssignment')} />
              </label>
              <select
                name="group_assignment_mode"
                value={formData.group_assignment_mode}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(206, 212, 218, 0.7)',
                  backgroundColor: 'rgba(245, 242, 239, 0.9)',
                  color: '#0F172A',
                  fontSize: '1rem',
                  marginTop: '8px'
                }}
                required
              >
                <option value="auto">{t('create.groupAuto')}</option>
                <option value="manual">{t('create.groupManual')}</option>
              </select>
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {formData.group_assignment_mode === 'auto'
                  ? t('create.helpGroupAuto')
                  : t('create.helpGroupManual')}
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                {t('create.teamsAdvance')}
                <Tooltip text={t('create.tooltipTeamsAdvance')} />
              </label>
              <select
                name="teams_advance_from_group"
                value={formData.teams_advance_from_group}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(206, 212, 218, 0.7)',
                  backgroundColor: 'rgba(245, 242, 239, 0.9)',
                  color: '#0F172A',
                  fontSize: '1rem',
                  marginTop: '8px'
                }}
                required
              >
                <option value="2">{t('create.teamsAdvance2')}</option>
                <option value="3">{t('create.teamsAdvance3')}</option>
                <option value="4">{t('create.teamsAdvance4')}</option>
                <option value="best_plus_runners">{t('create.teamsAdvanceBestRunners')}</option>
              </select>
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {formData.teams_advance_from_group === 'best_plus_runners'
                  ? t('create.helpTeamsAdvanceBestRunners')
                  : t('create.helpTeamsAdvance')}
              </small>
            </div>

          </>
        )}

        {formData.tournament_type === 'ko_placement' && (
          <div className="form-group" style={{
            backgroundColor: 'rgba(59, 130, 246, 0.06)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid rgba(59, 130, 246, 0.25)',
            marginTop: '4px'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              {t('create.drawMode')}
              <Tooltip text={t('create.tooltipDrawMode')} />
            </label>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: formData.draw_mode === 'random' ? '600' : '400' }}>
                <input
                  type="radio"
                  name="draw_mode"
                  value="random"
                  checked={formData.draw_mode === 'random'}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>{t('create.drawRandom')}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: formData.draw_mode === 'manual' ? '600' : '400' }}>
                <input
                  type="radio"
                  name="draw_mode"
                  value="manual"
                  checked={formData.draw_mode === 'manual'}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>{t('create.drawManual')}</span>
              </label>
            </div>
            <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '8px' }}>
              {formData.draw_mode === 'manual'
                ? t('create.helpDrawManual')
                : t('create.helpDrawRandom')}
            </small>
          </div>
        )}

        {(formData.tournament_type === 'groups' || formData.tournament_type === 'ko_placement') && (
          <div className="form-group" style={{
            backgroundColor: 'rgba(232, 183, 58, 0.08)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid rgba(232, 183, 58, 0.3)',
            marginTop: '20px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '16px', color: '#0F172A' }}>{t('create.scheduleSection')}</h4>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                {t('create.startTime')}
                <Tooltip text={t('create.tooltipStartTime')} />
              </label>
              <input
                name="start_time"
                type="time"
                value={formData.start_time}
                onChange={handleChange}
                placeholder={t('create.placeholderStartTime')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(206, 212, 218, 0.7)',
                  backgroundColor: 'white',
                  color: '#0F172A',
                  fontSize: '1rem'
                }}
              />
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {t('create.helpStartTime')}
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                {t('create.matchDuration')}
                <Tooltip text={t('create.tooltipMatchDuration')} />
              </label>
              <input
                name="match_duration"
                type="number"
                min="10"
                max="60"
                value={formData.match_duration}
                onChange={handleChange}
                placeholder={t('create.placeholderMatchDuration')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(206, 212, 218, 0.7)',
                  backgroundColor: 'white',
                  color: '#0F172A',
                  fontSize: '1rem'
                }}
              />
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {t('create.helpMatchDuration')}
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                {t('create.breakDuration')}
                <Tooltip text={t('create.tooltipBreakDuration')} />
              </label>
              <input
                name="break_duration"
                type="number"
                min="0"
                max="90"
                value={formData.break_duration}
                onChange={handleChange}
                placeholder={t('create.placeholderBreakDuration')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(206, 212, 218, 0.7)',
                  backgroundColor: 'white',
                  color: '#0F172A',
                  fontSize: '1rem'
                }}
              />
              <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                {t('create.helpBreakDuration')}
              </small>
            </div>
          </div>
        )}

        {formData.tournament_type === 'ko' && (
          <div className="form-group" style={{
            backgroundColor: 'rgba(108, 117, 125, 0.05)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid rgba(108, 117, 125, 0.15)'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: '12px',
              marginBottom: 0
            }}>
              <input
                name="ko_consolation_round"
                type="checkbox"
                checked={formData.ko_consolation_round}
                onChange={handleChange}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '1rem', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                {t('create.consolation')}
                <Tooltip text={t('create.tooltipConsolation')} />
              </span>
            </label>
            <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '8px', marginLeft: '32px' }}>
              {t('create.helpConsolation')}
            </small>
          </div>
        )}

        {formData.tournament_type === 'mexicano' && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center' }}>
              {t('create.numRounds')}
              <Tooltip text={t('create.tooltipNumRounds')} />
            </label>
            <input
              name="max_rounds"
              type="number"
              min="1"
              max="20"
              value={formData.max_rounds}
              onChange={handleChange}
              required
              placeholder={t('create.placeholderNumRounds')}
            />
            <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
              {t('create.helpNumRounds')}
            </small>
          </div>
        )}

        {formData.tournament_type === 'americano' && (
          <div className="form-group">
            <label>{t('create.numRoundsAuto')}</label>
            <input
              type="text"
              value={`${formData.num_teams > 0 ? formData.num_teams - 1 : 0} (${t('create.helpAmericanoRoundsAuto')})`}
              disabled
              style={{
                backgroundColor: 'var(--input-disabled-bg, #e9ecef)',
                color: 'var(--input-disabled-color, #6c757d)',
                cursor: 'not-allowed'
              }}
            />
            <small style={{ color: '#6c757d', fontSize: '0.8rem' }}>
              {t('create.helpAmericanoRounds', { n: formData.num_teams > 0 ? formData.num_teams - 1 : 0 })}
            </small>
          </div>
        )}

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center' }}>
            {t('create.numCourts')}
            <Tooltip text={t('create.tooltipNumCourts')} />
          </label>
          <input
            name="num_courts"
            type="number"
            min="1"
            max={maxCourts || 20}
            value={formData.num_courts}
            onChange={handleChange}
            required
            placeholder={t('create.placeholderNumCourts')}
          />
          {maxCourts && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted, #888)' }}>
              Dein Plan erlaubt maximal {maxCourts} Courts.
            </p>
          )}
        </div>

        {courtNames.length > 0 && (
          <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
            <h4 style={{ marginTop: 0, marginBottom: '12px' }}>{t('create.courtNames')}</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {courtNames.map((name, idx) => (
                <div key={idx} className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem' }}>Court {idx + 1}</label>
                  <input
                    value={name}
                    onChange={(e) => handleCourtNameChange(idx, e.target.value)}
                    placeholder={`Court ${idx + 1}`}
                    required
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Best of 3 Option - NICHT für Americano/Mexicano */}
        {formData.tournament_type !== 'americano' && formData.tournament_type !== 'mexicano' && (
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}>
              <input
                type="checkbox"
                name="best_of_three"
                checked={formData.best_of_three}
                onChange={handleChange}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {t('create.bestOfThree')}
                <Tooltip text={t('create.tooltipBestOfThree')} />
              </span>
            </label>
            <div style={{
              marginTop: '5px',
              fontSize: '14px',
              color: '#64748b',
              marginLeft: '30px'
            }}>
              {t('create.helpBestOfThree')}
            </div>
          </div>
        )}

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '16px', fontWeight: '500' }}>
            <input
              type="checkbox"
              name="show_in_presentation"
              checked={formData.show_in_presentation}
              onChange={handleChange}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {t('create.showInPresentation')}
              <Tooltip text={t('create.tooltipShowInPresentation')} />
            </span>
          </label>
          <div style={{ marginTop: '5px', fontSize: '14px', color: '#64748b', marginLeft: '30px' }}>
            {t('create.helpShowInPresentation')}
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center' }}>
            {t('create.description')}
            <Tooltip text={t('create.tooltipDescription')} />
          </label>
          <textarea
            name="description"
            rows={3}
            value={formData.description}
            onChange={handleChange}
            placeholder={t('create.placeholderDescription')}
          />
        </div>

        <button type="submit" disabled={loading}>
          {loading ? t('create.submitting') : t('create.submit')}
        </button>
      </form>

      {message && <p style={{ color: '#22c55e', marginTop: 16 }}>{message}</p>}
      {error && <p style={{ color: '#f87171', marginTop: 16 }}>{error}</p>}
    </div>
  );
}

export default TournamentCreate;
